import React, { useState, useEffect } from 'react';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';
import { useAuth } from './useAuth';
import { analyticsService } from '../services/upgradeAnalyticsService';

/**
 * FreeUserMatchLimiter Component
 * Manages the "1 free match" feature for free-tier employers
 * Tracks viewed matches and shows upgrade prompt after limit reached
 * 
 * Usage:
 * const { viewedMatchCount, canViewMore, recordMatchViewed } = useFreeUserMatchLimiter();
 * if (canViewMore) { showMatch() } else { showUpgradePrompt() }
 */
export const useFreeUserMatchLimiter = () => {
  const { user, userProfile } = useAuth();
  const [viewedMatchCount, setViewedMatchCount] = useState(0);
  const [sessionData, setSessionData] = useState(null);

  // Initialize from localStorage on mount
  useEffect(() => {
    if (!user?.id) return;

    // Check if user is free tier
    const isFreeTier = userProfile?.subscription_tier === 'free';
    if (!isFreeTier) {
      setViewedMatchCount(Infinity); // Unlimited for paid users
      return;
    }

    // Load session data from localStorage
    const sessionKey = `free_user_matches_${user.id}`;
    const stored = localStorage.getItem(sessionKey);
    
    if (stored) {
      const data = JSON.parse(stored);
      
      // Check if session is still valid (within 24 hours)
      const sessionAge = Date.now() - data.timestamp;
      const isSessionValid = sessionAge < 24 * 60 * 60 * 1000; // 24 hours
      
      if (isSessionValid) {
        setViewedMatchCount(data.matchCount);
        setSessionData(data);
      } else {
        // Reset session after 24 hours
        resetMatchCounter();
      }
    } else {
      // Initialize new session
      initializeSession();
    }
  }, [user?.id, userProfile?.subscription_tier]);

  const initializeSession = () => {
    if (!user?.id) return;

    const sessionKey = `free_user_matches_${user.id}`;
    const sessionData = {
      matchCount: 0,
      timestamp: Date.now(),
      userId: user.id,
      date: new Date().toISOString().split('T')[0]
    };

    localStorage.setItem(sessionKey, JSON.stringify(sessionData));
    setViewedMatchCount(0);
    setSessionData(sessionData);
  };

  const recordMatchViewed = async (candidateId, jobId) => {
    if (!user?.id) return false;

    // Check if free tier
    const isFreeTier = userProfile?.subscription_tier === 'free';
    if (!isFreeTier) return true; // Paid users can view unlimited

    const newCount = viewedMatchCount + 1;
    const sessionKey = `free_user_matches_${user.id}`;

    // Update localStorage
    const updatedSession = {
      ...sessionData,
      matchCount: newCount,
      lastViewedCandidateId: candidateId,
      timestamp: Date.now()
    };

    localStorage.setItem(sessionKey, JSON.stringify(updatedSession));
    setViewedMatchCount(newCount);

    // Track analytics event
    analyticsService.trackCandidateMatchViewed(user.id, jobId, candidateId, {
      matchNumber: newCount,
      freeUserView: true
    });

    // Return true if they can view more, false if limit reached
    return newCount < 1; // They can view 1 match free
  };

  const resetMatchCounter = () => {
    if (!user?.id) return;
    const sessionKey = `free_user_matches_${user.id}`;
    localStorage.removeItem(sessionKey);
    initializeSession();
  };

  const canViewMore = () => {
    const isFreeTier = userProfile?.subscription_tier === 'free';
    if (!isFreeTier) return true; // Paid users can always view more
    return viewedMatchCount < 1; // Free users can only view 1
  };

  const getRemainingMatches = () => {
    const isFreeTier = userProfile?.subscription_tier === 'free';
    if (!isFreeTier) return Infinity;
    return Math.max(0, 1 - viewedMatchCount);
  };

  return {
    viewedMatchCount,
    canViewMore: canViewMore(),
    remainingMatches: getRemainingMatches(),
    recordMatchViewed,
    resetMatchCounter,
    isFreeTier: userProfile?.subscription_tier === 'free',
    sessionData
  };
};

/**
 * FreeUserMatchLimitBanner Component
 * Displays a banner showing free-tier match usage
 */
export const FreeUserMatchLimitBanner = ({ matchCount, remainingMatches, isFreeTier }) => {
  if (!isFreeTier) return null;

  const hasViewedMatch = matchCount > 0;

  return (
    <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Icon name="AlertCircle" size={20} className="text-amber-600" />
        <div>
          <p className="font-semibold text-text-primary">
            {hasViewedMatch 
              ? 'You\'ve viewed your 1 free candidate match' 
              : 'You have 1 free candidate match available'}
          </p>
          <p className="text-sm text-text-secondary">
            {hasViewedMatch 
              ? 'Upgrade your plan to view more AI-recommended candidates' 
              : 'After viewing 1 match, you\'ll need to upgrade to see more'}
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * UpgradeToViewMoreComponent
 * Replaces candidate cards when free-tier match limit is reached
 */
export const UpgradeToViewMoreCard = ({ onUpgradeClick }) => {
  return (
    <div className="bg-white rounded-lg border border-gray-300 p-8 text-center">
      <div className="mb-4">
        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Lock" size={32} className="text-primary" />
        </div>
      </div>

      <h3 className="text-xl font-bold text-text-primary mb-2">
        View More Candidates
      </h3>

      <p className="text-text-secondary mb-6">
        You've viewed your 1 free AI-recommended candidate match. Upgrade your plan to see more qualified candidates tailored to your job.
      </p>

      <div className="bg-blue-50 rounded-lg p-4 mb-6 text-left">
        <p className="text-sm text-text-secondary mb-3">
          <strong>With a paid plan, you get:</strong>
        </p>
        <ul className="space-y-2 text-sm">
          <li className="flex items-center gap-2">
            <Icon name="Check" size={16} className="text-green-500" />
            <span className="text-text-secondary">Unlimited AI candidate recommendations</span>
          </li>
          <li className="flex items-center gap-2">
            <Icon name="Check" size={16} className="text-green-500" />
            <span className="text-text-secondary">Advanced filtering and search</span>
          </li>
          <li className="flex items-center gap-2">
            <Icon name="Check" size={16} className="text-green-500" />
            <span className="text-text-secondary">Hiring analytics and insights</span>
          </li>
          <li className="flex items-center gap-2">
            <Icon name="Check" size={16} className="text-green-500" />
            <span className="text-text-secondary">Priority support</span>
          </li>
        </ul>
      </div>

      <Button
        variant="primary"
        onClick={onUpgradeClick}
        iconName="TrendingUp"
        className="w-full"
      >
        View Pricing & Upgrade
      </Button>
    </div>
  );
};

export default useFreeUserMatchLimiter;
